import {
  Menu,
  Tag,
  type DataTableColumn,
} from '@noorden-insurtech/alba-design-system';
import { CalendarDays, FileSpreadsheet, Printer } from 'lucide-react';
import { formatCurrency, formatDateToBrazil } from '../../../shared/utils';
import { formatRegisterNumber } from '../../../shared/utils/format-register-number';
import type { Receipt, ReceiptStatus } from '../../../services/financial/types';

type TagColorScheme = 'success' | 'warning';

const receiptStatusConfig: Record<
  ReceiptStatus,
  { label: string; colorScheme: TagColorScheme }
> = {
  paid: { label: 'Pagos', colorScheme: 'success' },
  pending: { label: 'Pendente', colorScheme: 'warning' },
};

export type ReceiptAction = 'pdf' | 'excel';

const renderDateCell = (value?: string) => {
  if (!value) {
    return <span className='text-alba-gray-500 text-sm'>—</span>;
  }
  return (
    <div className='flex items-center gap-1.5 text-alba-gray-700 text-sm'>
      <CalendarDays className='h-4 w-4 shrink-0 text-alba-gray-600' />
      <span>{formatDateToBrazil(value)}</span>
    </div>
  );
};

const receiptColumn: DataTableColumn<Receipt> = {
  accessorKey: 'receiptNumber',
  header: 'RECIBO',
  size: 100,
  cell: ({ row }) => (
    <span className='font-gotham font-bold text-alba-black-500'>
      {row.original.receiptNumber}
    </span>
  ),
};

/** Coluna FAVORECIDO (nome + CPF/CNPJ) — só para o perfil Seguradora. */
const favorecidoColumn: DataTableColumn<Receipt> = {
  accessorKey: 'favorecidoName',
  header: 'FAVORECIDO',
  size: 220,
  sortable: false,
  cell: ({ row }) => {
    const { favorecidoName, favorecidoDocument } = row.original;
    return (
      <div className='flex flex-col gap-0.5'>
        <span className='font-gotham font-bold text-alba-black-500'>
          {favorecidoName || '—'}
        </span>
        {favorecidoDocument && (
          <span className='text-xs text-alba-gray-600'>
            {formatRegisterNumber(favorecidoDocument)}
          </span>
        )}
      </div>
    );
  },
};

export function createReceiptsColumns(
  isInsurer: boolean,
  // eslint-disable-next-line no-unused-vars
  onAction: (_action: ReceiptAction, _receipt: Receipt) => void
): DataTableColumn<Receipt>[] {
  return [
    receiptColumn,
    ...(isInsurer ? [favorecidoColumn] : []),
    {
      accessorKey: 'netValue',
      header: 'VALOR LÍQUIDO',
      size: 160,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm font-medium tabular-nums'>
          {formatCurrency(row.original.netValue)}
        </span>
      ),
    },
    {
      accessorKey: 'paymentForecast',
      header: 'PREVISÃO',
      size: 160,
      sortable: false,
      cell: ({ row }) => renderDateCell(row.original.paymentForecast),
    },
    {
      accessorKey: 'paymentDate',
      header: 'PAGAMENTO',
      size: 160,
      sortable: false,
      cell: ({ row }) => renderDateCell(row.original.paymentDate),
    },
    {
      accessorKey: 'status',
      header: 'STATUS',
      size: 130,
      sortable: false,
      cell: ({ row }) => {
        const config = receiptStatusConfig[row.original.status];
        return (
          <Tag.Root
            variant='outline'
            colorScheme={config.colorScheme}
            size='sm'
          >
            <Tag.Label>{config.label}</Tag.Label>
          </Tag.Root>
        );
      },
    },
    {
      id: 'actions',
      header: 'AÇÕES',
      size: 90,
      sortable: false,
      cell: ({ row }) => {
        const receipt = row.original;
        return (
          <div className='flex justify-end'>
            <Menu.Root>
              <Menu.Trigger />
              <Menu.Content>
                <Menu.Item
                  value='pdf'
                  icon={<Printer size={16} />}
                  onSelect={() => onAction('pdf', receipt)}
                >
                  Recibo em PDF
                </Menu.Item>
                <Menu.Item
                  value='excel'
                  icon={<FileSpreadsheet size={16} />}
                  onSelect={() => onAction('excel', receipt)}
                >
                  Detalhes em Excel
                </Menu.Item>
              </Menu.Content>
            </Menu.Root>
          </div>
        );
      },
    },
  ];
}
